import { useState, useEffect, useMemo, useRef } from 'react';
import LocationSelect from '../components/shared/LocationSelect.jsx';
import jsQR from 'jsqr';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiPrompt } from '../utils/dialogs.js';

// ── Shared UI primitives (matches Warehouse.jsx / Production.jsx style) ───
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
    transition: 'opacity .15s',
  };
  const v = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
  };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 480, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}

const STATUS_C = {
  initiated: '#6b7280', counting: '#3b82f6', variance_review: '#f59e0b',
  signed_off: '#22c55e', cancelled: '#dc2626',
};
function StatusBadge({ status }) {
  const c = STATUS_C[status] || '#6b7280';
  return <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', background: c + '22', color: c }}>{status.replace(/_/g, ' ')}</span>;
}

const CATEGORIES = ['raw_material', 'semi_product', 'bulk_finished', 'bottle_finished', 'packaging', 'consumable', 'by_product', 'client_stock'];

// ── New stock take modal ──────────────────────────────────────────────────
function NewStockTakeModal({ onClose, onCreated }) {
  const [takeType, setTakeType] = useState('full');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const { stock_take } = await apiFetch('/stock-take', { method: 'POST', body: { take_type: takeType } });
      onCreated(stock_take.id);
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="New Stock Take" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Type">
          <Select value={takeType} onChange={e => setTakeType(e.target.value)}>
            <option value="full">Full — every zone (use this for the first walk-round)</option>
            <option value="rolling">Rolling — a subset of zones/locations</option>
          </Select>
        </Field>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 14px' }}>
          Since nothing's recorded in the system yet, everything you count will show as a "gain" —
          that's expected for a first pass. Resolving each as "adjusted" sets it as the starting stock level.
        </p>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Starting…' : 'Start Stock Take'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Add count modal ────────────────────────────────────────────────────────
function AddCountModal({ stockTakeId, locations, onClose, onSaved }) {
  const { data: zoneData } = useApi('/warehouse/zones');
  const zones = zoneData?.zones ?? zoneData ?? [];

  const [itemSearch, setItemSearch] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', category: 'packaging', unit: 'unit' });
  // Zone/bay stay set across successive adds (see submit()) — you're almost
  // always counting several items in the same bay in a row, and re-picking
  // the same zone (let alone hunting it out of a flat list of every bay in
  // every zone — 500+ options when Cow Shed's racks are all in there) every
  // single time was the #1 annoyance in this flow. Cascading zone → bay
  // instead of one flat location list also means two zones that happen to
  // share a name (there are two "Duty Paid Area" zones — one real, one a
  // virtual bookkeeping-only zone) show up as distinct, clearly-labelled
  // entries instead of two identical-looking rows in one big list.
  const [zoneId, setZoneId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [justAdded, setJustAdded] = useState(null); // last-added item name, for the inline confirmation
  // Missing ABV / bottle size, asked for while counting so the W1 gets its
  // litres without a separate tidy-up pass. Saved to the stock item itself.
  const [abvIn, setAbvIn] = useState('');
  const [dutyIn, setDutyIn] = useState('in_bond');
  const [lpuIn, setLpuIn] = useState('');
  const isLitreUnit = (u) => ['litres', 'litre', 'l', 'liters', 'liter', 'ltr', 'ml'].includes(String(u || '').trim().toLowerCase());
  const needsAbv = !!selectedItem?.is_alcoholic && !(Number(selectedItem.abv) > 0);
  const needsLpu = !!selectedItem?.is_alcoholic && !isLitreUnit(selectedItem.unit) && !(Number(selectedItem.litres_per_unit) > 0);

  // Bays within the selected zone only — not everything in the warehouse.
  const bays = useMemo(() => locations.filter(l => l.zone_id === zoneId), [locations, zoneId]);

  function selectZone(id) {
    setZoneId(id);
    // A zone with exactly one bay (most non-rack zones — Bottling Room,
    // Still House, both Duty Paid Areas, etc.) doesn't need a second
    // dropdown asking you to pick the only option; auto-select it. A zone
    // with several bays (the Cow Shed racks) clears it so you have to
    // actually choose.
    const zoneBays = locations.filter(l => l.zone_id === id);
    setLocationId(zoneBays.length === 1 ? zoneBays[0].id : '');
  }

  useEffect(() => {
    if (!itemSearch.trim()) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(itemSearch)}`);
        setItems(stock_items || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [itemSearch]);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr(''); setJustAdded(null);
    try {
      let stockItemId = selectedItem?.id;
      if (creatingNew) {
        if (!newItem.name.trim()) throw new Error('Item name is required');
        const { stock_item } = await apiFetch('/stock', { method: 'POST', body: newItem });
        stockItemId = stock_item.id;
      }
      if (!locationId) throw new Error('Choose where it was counted — a count with no location can\'t be put into stock');
      if (!stockItemId) throw new Error('Pick an existing item or add a new one');
      if (!qty) throw new Error('Quantity is required');
      if (!creatingNew && (needsAbv || needsLpu)) {
        if (needsAbv && !(parseFloat(abvIn) > 0)) throw new Error(`${selectedItem.name} has no ABV in Blake yet — enter it so the W1 gets its litres`);
        if (needsLpu && !(parseFloat(lpuIn) > 0)) throw new Error(`${selectedItem.name} has no bottle size in Blake yet — enter the litres per ${selectedItem.unit || 'unit'}`);
        const body = {};
        if (needsAbv) body.abv = parseFloat(abvIn);
        if (needsLpu) body.litres_per_unit = parseFloat(lpuIn);
        await apiFetch(`/stock/${stockItemId}`, { method: 'PUT', body });
      }

      await apiFetch(`/stock-take/${stockTakeId}/counts`, {
        method: 'POST',
        body: { stockItemId, locationId: locationId || null, countedQuantity: parseFloat(qty),
          dutyStatus: (creatingNew ? newItem.is_alcoholic : selectedItem?.is_alcoholic) ? dutyIn : undefined },
      });
      onSaved();

      // Reset the per-item fields for the next count, but deliberately
      // leave locationId as-is and keep the modal open — see note above.
      setJustAdded(selectedItem?.name || newItem.name);
      setItemSearch('');
      setSelectedItem(null);
      setCreatingNew(false);
      setNewItem({ name: '', category: 'packaging', unit: 'unit' });
      setQty('');
      setAbvIn(''); setLpuIn(''); setDutyIn('in_bond');
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add Count" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Location" hint="Pick the room; choose a bay too if it is in one.">
          <LocationSelect locations={locations} value={locationId} onChange={e => setLocationId(e.target.value)} placeholder="— choose a location —" required />
        </Field>

        {!creatingNew ? (
          <Field label="Item">
            <Input placeholder="Search existing items…" value={itemSearch} onChange={e => { setItemSearch(e.target.value); setSelectedItem(null); }} />
            {items.length > 0 && !selectedItem && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
                {items.map(i => (
                  <div key={i.id} onClick={() => { setSelectedItem(i); setItemSearch(i.name); setItems([]); }}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                    {i.name} <span style={{ color: 'var(--text-muted)' }}>({i.sku})</span>
                  </div>
                ))}
              </div>
            )}
            {selectedItem && <p style={{ fontSize: 12, color: 'var(--pld-green)', margin: '4px 0 0' }}>✓ {selectedItem.name} selected</p>}
            {selectedItem && (needsAbv || needsLpu) && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>Not in Blake yet: needed for the W1 litres. It's saved to the item, so you only enter it once.</div>
                <div style={{ display: 'grid', gridTemplateColumns: needsAbv && needsLpu ? '1fr 1fr' : '1fr', gap: 10 }}>
                  {needsAbv && <Field label="ABV %"><Input type="number" step="0.01" min="0" max="100" value={abvIn} onChange={e => setAbvIn(e.target.value)} placeholder="e.g. 40" /></Field>}
                  {needsLpu && <Field label={`Litres per ${selectedItem.unit || 'unit'}`}><Input type="number" step="0.001" min="0" value={lpuIn} onChange={e => setLpuIn(e.target.value)} placeholder="e.g. 0.7" /></Field>}
                </div>
              </div>
            )}
            <button type="button" onClick={() => { setCreatingNew(true); setSelectedItem(null); }}
              style={{ background: 'none', border: 'none', color: 'var(--pld-green)', fontSize: 12, cursor: 'pointer', padding: '6px 0 0', textDecoration: 'underline' }}>
              Can't find it — add a new item
            </button>
          </Field>
        ) : (
          <>
            <Field label="New item name *"><Input value={newItem.name} onChange={e => setNewItem(f => ({ ...f, name: e.target.value }))} required /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Category">
                <Select value={newItem.category} onChange={e => setNewItem(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </Select>
              </Field>
              <Field label="Unit"><Input value={newItem.unit} onChange={e => setNewItem(f => ({ ...f, unit: e.target.value }))} placeholder="e.g. unit, case, kg" /></Field>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '2px 0 8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!newItem.is_alcoholic} onChange={e => setNewItem(f => ({ ...f, is_alcoholic: e.target.checked }))} />
              Contains alcohol
            </label>
            <div style={{ display: newItem.is_alcoholic ? 'grid' : 'none', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="ABV %"><Input type="number" step="0.01" min="0" max="100" value={newItem.abv ?? ''} onChange={e => setNewItem(f => ({ ...f, abv: e.target.value }))} placeholder="e.g. 40" /></Field>
              <Field label="Litres per unit"><Input type="number" step="0.001" min="0" value={newItem.litres_per_unit ?? ''} onChange={e => setNewItem(f => ({ ...f, litres_per_unit: e.target.value }))} placeholder="e.g. 0.7 (blank if counted in litres)" /></Field>
            </div>
            <button type="button" onClick={() => setCreatingNew(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: '0 0 10px', textDecoration: 'underline' }}>
              ← search existing items instead
            </button>
          </>
        )}

        <Field label="Quantity counted *"><Input type="number" step="any" min="0" value={qty} onChange={e => setQty(e.target.value)} required /></Field>

        {(creatingNew ? newItem.is_alcoholic : selectedItem?.is_alcoholic) && (
          <Field label="Is it in bond or duty paid? (the stock's own status, not the room's)">
            <Select value={dutyIn} onChange={e => setDutyIn(e.target.value)}>
              <option value="in_bond">In bond</option>
              <option value="duty_paid">Duty paid</option>
            </Select>
          </Field>
        )}

        {justAdded && !err && (
          <p style={{ fontSize: 12, color: 'var(--pld-green)', fontWeight: 600, margin: '0 0 12px' }}>✓ {justAdded} added — same bay, add the next item below.</p>
        )}
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Done</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Count'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Scan pallet / batch modal ───────────────────────────────────────────────
// Opens the device camera and watches for a QR code — either a pallet's
// unit_ref (bulk-counts everything currently on that pallet) or a goods
// receipt's batch_ref (counts that batch at wherever it currently sits,
// stock_batch_levels — see batchLabelPdfService.js). Every batch label
// prints a "LOT-YYYY-NNNNN" ref, so that prefix alone tells this apart
// from a pallet's "PLT-..." ref without needing two separate scanners.
// Stays open after each successful scan so you can keep working down a
// row of pallets/batches without re-opening it each time.
function ScanPalletModal({ stockTakeId, onClose, onScanned }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const busyRef = useRef(false);

  const [cameraErr, setCameraErr] = useState('');
  const [scanning, setScanning] = useState(true);
  const [lastResult, setLastResult] = useState(null); // { unit_ref, items_counted } | { error }
  const [manualRef, setManualRef] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        tick();
      } catch (e) {
        setCameraErr('Could not access the camera (' + e.message + '). You can still type the pallet code below.');
      }
    }

    function tick() {
      if (cancelled) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA && !busyRef.current) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code && code.data) {
          handleScanned(code.data);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleScanned(ref) {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const isBatch = /^LOT-/i.test(ref.trim());
      const result = isBatch
        ? await apiFetch(`/stock-take/${stockTakeId}/scan-batch`, { method: 'POST', body: { batchRef: ref.trim() } })
        : await apiFetch(`/stock-take/${stockTakeId}/scan-pallet`, { method: 'POST', body: { unitRef: ref.trim() } });
      setLastResult({ unit_ref: result.unit_ref || result.batch_ref, items_counted: result.items_counted });
      onScanned();
    } catch (e) {
      setLastResult({ error: e.message, unit_ref: ref });
    } finally {
      // Small pause before accepting the next scan so the same label isn't
      // immediately re-read while still in frame.
      setTimeout(() => { busyRef.current = false; }, 1500);
    }
  }

  async function submitManual(e) {
    e.preventDefault();
    if (!manualRef.trim()) return;
    await handleScanned(manualRef.trim());
    setManualRef('');
  }

  return (
    <Modal title="Scan Pallet / Batch" onClose={onClose} width={420}>
      {cameraErr ? (
        <p style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{cameraErr}</p>
      ) : (
        <div style={{ position: 'relative', borderRadius: 'var(--radius)', overflow: 'hidden', background: '#000', marginBottom: 12 }}>
          <video ref={videoRef} muted playsInline style={{ width: '100%', display: 'block' }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, border: '3px solid rgba(255,220,6,.6)', pointerEvents: 'none' }} />
        </div>
      )}

      {lastResult && (
        lastResult.error ? (
          <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px' }}>
            {lastResult.unit_ref}: {lastResult.error}
          </p>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--pld-green)', fontWeight: 600, margin: '0 0 12px' }}>
            ✓ {lastResult.unit_ref}: {lastResult.items_counted} item{lastResult.items_counted === 1 ? '' : 's'} counted
          </p>
        )
      )}

      <form onSubmit={submitManual} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        <Input placeholder="Or type a pallet (PLT-…) or batch (LOT-…) ref" value={manualRef} onChange={e => setManualRef(e.target.value)} />
        <Btn type="submit" size="sm">Count</Btn>
      </form>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0' }}>
        Point the camera at a pallet or batch label. It keeps scanning — close this when you're done with this row.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <Btn variant="ghost" onClick={onClose}>Done</Btn>
      </div>
    </Modal>
  );
}

// ── Stock take detail ──────────────────────────────────────────────────────
// Give a count with no location its location (before sign-off), or put a
// signed-off count that never reached stock into stock at that location.
function PlaceCount({ stockTakeId, snapshotId, varianceId, locations, label = 'Set location', onDone }) {
  const [open, setOpen] = useState(false);
  const [loc, setLoc] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(varianceId
        ? `/stock-take/${stockTakeId}/variances/${varianceId}/place`
        : `/stock-take/${stockTakeId}/snapshots/${snapshotId}/location`, { method: 'POST', body: { location_id: loc } });
      setOpen(false); onDone?.(r?.data);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  if (!open) return <Btn size="sm" variant="ghost" onClick={() => setOpen(true)} style={{ color: '#991b1b' }}>{label}</Btn>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', minWidth: 280 }}>
      <span style={{ flex: 1, minWidth: 200 }}><LocationSelect locations={locations} value={loc} onChange={e => setLoc(e.target.value)} placeholder="— choose a location —" /></span>
      <Btn size="sm" onClick={save} disabled={!loc || busy}>{busy ? 'Saving…' : 'Save'}</Btn>
      <Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
      {err && <span style={{ fontSize: 12, color: '#dc2626', width: '100%' }}>{err}</span>}
    </span>
  );
}

function StockTakeDetail({ id, onBack, currentUser }) {
  const { data, refetch } = useApi(`/stock-take/${id}`, [id]);
  const { data: locData } = useApi('/warehouse/locations');
  const locations = locData?.locations ?? locData ?? [];
  const { data: pendingData, refetch: refetchPending } = useApi(`/stock-take/${id}/pending-approval`, [id]);
  const pendingApproval = pendingData?.approval ?? null;

  const [showAddCount, setShowAddCount] = useState(false);
  const [showScanPallet, setShowScanPallet] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dutyFlags, setDutyFlags] = useState(null);
  const [litresFix, setLitresFix] = useState(null);
  const [placed, setPlaced] = useState([]); // counts put into stock from the warning box

  // Hooks must run unconditionally (same order every render) — compute
  // against safe fallbacks rather than early-returning before these run.
  const counts = data?.counts ?? [];
  const variances = data?.variances ?? [];
  const snapshots = data?.snapshots ?? [];

  const countsBySnapshot = useMemo(() => {
    const m = {};
    counts.forEach(c => { m[c.snapshot_id] = c; });
    return m;
  }, [counts]);

  // Keyed by snapshot_id, not stock_item_id — the same item can be
  // snapshotted at more than one location in a stock take, which used to
  // collapse to a single (arbitrary) variance here. Falls back to
  // stock_item_id only for pre-migration rows that predate snapshot_id.
  const variancesBySnapshot = useMemo(() => {
    const m = {};
    variances.forEach(v => { m[v.snapshot_id || `item:${v.stock_item_id}`] = v; });
    return m;
  }, [variances]);

  if (!data) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  const { take } = data;

  const countedRows = snapshots.filter(s => countsBySnapshot[s.id]);
  const uncountedRows = snapshots.filter(s => !countsBySnapshot[s.id]);
  const pendingVariances = variances.filter(v => v.resolution === 'pending');

  async function calcVariances() {
    setBusy(true); setErr('');
    try { await apiFetch(`/stock-take/${id}/calculate-variances`, { method: 'POST' }); refetch(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function resolve(varianceId, resolution) {
    setBusy(true); setErr('');
    try { await apiFetch(`/stock-take/${id}/variances/${varianceId}/resolve`, { method: 'POST', body: { resolution } }); refetch(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function fixMissingLitres() {
    setErr('');
    try {
      const r = await apiFetch(`/stock-take/${id}/fix-missing-litres`, { method: 'POST', body: {} });
      setLitresFix(r.data); refetch();
    } catch (e) { setErr(e.message); }
  }

  async function requestSignOff() {
    setBusy(true); setErr('');
    try { await apiFetch(`/stock-take/${id}/request-sign-off`, { method: 'POST' }); refetchPending(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function approveSignOff() {
    setBusy(true); setErr('');
    try {
      const { stock_take } = await apiFetch(`/stock-take/${id}/approve-sign-off`, { method: 'POST', body: { approval_id: pendingApproval.id } });
      if (stock_take?.duty_status_flags?.length) setDutyFlags(stock_take.duty_status_flags);
      refetch(); refetchPending();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  // System owners can skip the request/approve flow entirely — everyone
  // else still needs a second person, per the standard two-person rule.
  async function signOffAsOwner() {
    setBusy(true); setErr('');
    try {
      const { stock_take } = await apiFetch(`/stock-take/${id}/sign-off-owner`, { method: 'POST' });
      if (stock_take?.duty_status_flags?.length) setDutyFlags(stock_take.duty_status_flags);
      refetch(); refetchPending();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function cancelStockTake() {
    setBusy(true); setErr('');
    try {
      await apiFetch(`/stock-take/${id}/cancel`, { method: 'POST', body: { reason: cancelReason || undefined } });
      setShowCancel(false); setCancelReason('');
      refetch(); refetchPending();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const cancellable = ['initiated', 'counting', 'variance_review'].includes(take?.status);

  async function reopenStockTake() {
    const reason = (await uiPrompt('Reopen this stock take to correct it?\n\nIts stock adjustments and W1 gains/losses are undone now, and applied again (corrected) when it\'s signed off again.\n\nReason (optional):'));
    if (reason === null) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(`/stock-take/${id}/reopen`, { method: 'POST', body: { reason: reason || undefined } });
      if (r.stock_take?.notes?.length) setErr(r.stock_take.notes.join(' '));
      refetch(); refetchPending();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12 }}>← All stock takes</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{take.stock_take_ref}</h2>
        <StatusBadge status={take.status} />
        {cancellable && (
          <Btn size="sm" variant="ghost" onClick={() => setShowCancel(true)} style={{ marginLeft: 'auto', color: '#dc2626' }}>
            Cancel stock take
          </Btn>
        )}
        {take.status === 'signed_off' && (
          <Btn size="sm" variant="ghost" disabled={busy} onClick={reopenStockTake} style={{ marginLeft: 'auto' }}>
            Reopen to correct
          </Btn>
        )}
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-muted)' }}>
        {take.take_type === 'full' ? 'Full stock take' : 'Rolling stock take'} · started {new Date(take.created_at).toLocaleString()}
        {take.status === 'cancelled' && take.cancelled_at && (
          <> · cancelled {new Date(take.cancelled_at).toLocaleString()}{take.cancellation_reason ? ` — ${take.cancellation_reason}` : ''}</>
        )}
      </p>

      {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}

      {placed.filter(Boolean).length > 0 && (
        <div style={{ padding: 14, border: '1px solid #16a34a', background: '#f0fdf4', borderRadius: 'var(--radius)', marginBottom: 20, fontSize: 13, color: '#166534' }}>
          {placed.filter(Boolean).map((d, i) => (
            <div key={i}>✓ Put {d.quantity > 0 ? '+' : ''}{d.quantity} {d.unit || ''} of <strong>{d.item_name}</strong> into stock at <strong>{d.location_code}</strong> — {d.location_code} now holds {d.now_at_location} {d.unit || ''}.</div>
          ))}
        </div>
      )}

      {variances.some(v => v.resolution_notes?.includes('stock_levels was NOT updated')) && (
        <div style={{ padding: 14, border: '1px solid #dc2626', background: '#fef2f2', borderRadius: 'var(--radius)', marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991b1b' }}>⚠ Stock levels still not updated for some counts</h4>
          <p style={{ fontSize: 12, color: '#991b1b', margin: 0 }}>
            These counts had no location, so they were never put into stock. Choose where each was counted and Blake puts it into stock there — no separate stock adjustment needed.
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#991b1b' }}>
            {variances.filter(v => v.resolution_notes?.includes('stock_levels was NOT updated')).map(v => (
              <li key={v.id} style={{ marginBottom: 6 }}>
                {v.item_name}: {v.variance_quantity > 0 ? '+' : ''}{Number(v.variance_quantity)} still missing{' '}
                <PlaceCount stockTakeId={id} varianceId={v.id} locations={locations} label="Choose location & put into stock"
                  onDone={(d) => { setDutyFlags(null); setPlaced(p => [...p, d]); refetch(); }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {variances.some(v => v.resolution_notes?.includes('NO LITRES')) && (
        <div style={{ padding: 14, border: '1px solid #dc2626', background: '#fef2f2', borderRadius: 'var(--radius)', marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991b1b' }}>⚠ Missing from the W1 return — no litres recorded</h4>
          <p style={{ fontSize: 12, color: '#991b1b', margin: 0 }}>
            When this stock take was signed off these items had no ABV / bottle size, so their gains/losses went to the W1 with zero litres.
            Once the ABV (and litres per bottle) are set on each item, click below to add the litres and recalculate the W1.
          </p>
          <div style={{ marginTop: 8 }}><Btn size="sm" onClick={fixMissingLitres}>Add the litres now</Btn></div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#991b1b' }}>
            {variances.filter(v => v.resolution_notes?.includes('NO LITRES')).map(v => (
              <li key={v.id}>{v.item_name}: {v.variance_quantity} recorded with 0 litres</li>
            ))}
          </ul>
        </div>
      )}

      {litresFix && (
        <div style={{ padding: 12, border: '1px solid var(--border)', background: 'var(--surface-2)', borderRadius: 'var(--radius)', marginBottom: 20, fontSize: 13 }}>
          {litresFix.fixed.length > 0 && <div>✓ Litres added: {litresFix.fixed.map(f => `${f.item} (${f.lpa} LPA)`).join(', ')}</div>}
          {litresFix.still_missing.length > 0 && <div style={{ color: '#991b1b' }}>Still missing: {litresFix.still_missing.map(f => `${f.item} — ${f.reason}`).join('; ')}</div>}
          {litresFix.cleared?.length > 0 && <div>✓ Warning cleared (this take was reopened, so these will get their litres when it is signed off again): {litresFix.cleared.join(', ')}</div>}
          {litresFix.recalculated.length > 0 && <div>W1 recalculated: {litresFix.recalculated.join(', ')}</div>}
          {litresFix.not_recalculated.length > 0 && <div style={{ color: '#b45309' }}>Not recalculated: {litresFix.not_recalculated.join('; ')}</div>}
        </div>
      )}

      {(take.status === 'counting' || take.status === 'variance_review') && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <Btn size="sm" onClick={() => setShowAddCount(true)}>+ Add Count</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setShowScanPallet(true)}>📷 Scan Pallet / Batch</Btn>
          {countedRows.length > 0 && (
            <Btn size="sm" variant="ghost" onClick={calcVariances} disabled={busy}>Calculate Variances</Btn>
          )}
        </div>
      )}

      {/* Counted items */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Counted ({countedRows.length})</h3>
      {countedRows.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>Nothing counted yet — walk the racks and add counts as you go.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '6px 8px' }}>Bay</th>
              <th style={{ padding: '6px 8px' }}>Pallet</th>
              <th style={{ padding: '6px 8px' }}>Item</th>
              <th style={{ padding: '6px 8px' }}>Counted</th>
              <th style={{ padding: '6px 8px' }}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {countedRows.map(s => {
              const c = countsBySnapshot[s.id];
              const v = variancesBySnapshot[s.id] || variancesBySnapshot[`item:${s.stock_item_id}`];
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>
                    {s.location_code || (['counting', 'variance_review'].includes(take.status)
                      ? <PlaceCount stockTakeId={id} snapshotId={s.id} locations={locations} onDone={refetch} />
                      : '—')}
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{s.pallet_ref || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{s.item_name}{s.duty_status === 'in_bond' || s.duty_status === 'duty_paid'
                    ? <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>({s.duty_status === 'in_bond' ? 'in bond' : 'duty paid'})</span> : null}</td>
                  <td style={{ padding: '6px 8px' }}>{c.counted_quantity} {s.unit}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {v ? (
                      <span style={{ color: Math.abs(v.variance_quantity) < 0.001 ? 'var(--text-muted)' : '#059669', fontWeight: 600 }}>
                        {v.variance_quantity > 0 ? '+' : ''}{v.variance_quantity} {v.resolution !== 'pending' ? `(${v.resolution})` : ''}
                      </span>
                    ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Variance resolution */}
      {pendingVariances.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Needs resolving ({pendingVariances.length})</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '6px 8px' }}>Item</th>
                <th style={{ padding: '6px 8px' }}>Location</th>
                <th style={{ padding: '6px 8px' }}>Expected</th>
                <th style={{ padding: '6px 8px' }}>Counted</th>
                <th style={{ padding: '6px 8px' }}>Variance</th>
                <th style={{ padding: '6px 8px' }}>Resolve</th>
              </tr>
            </thead>
            <tbody>
              {pendingVariances.map(v => (
                <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>{v.item_name}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{v.location_code || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{v.adjusted_expected}</td>
                  <td style={{ padding: '6px 8px' }}>{v.counted_quantity}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{v.variance_quantity > 0 ? '+' : ''}{v.variance_quantity}</td>
                  <td style={{ padding: '6px 8px', display: 'flex', gap: 4 }}>
                    <Btn size="sm" onClick={() => resolve(v.id, 'adjusted')} disabled={busy}>Set as stock</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => resolve(v.id, 'recount')} disabled={busy}>Recount</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Sign-off */}
      {take.status === 'variance_review' && pendingVariances.length === 0 && variances.length > 0 && (
        <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Sign-off</h3>
          {!pendingApproval ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>
                All variances resolved. Signing off applies them as the new stock levels
                {currentUser?.is_system_owner ? ' — as the owner account, you can sign off directly.' : ' — needs a second person to approve.'}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {currentUser?.is_system_owner && (
                  <Btn size="sm" onClick={signOffAsOwner} disabled={busy}>Sign Off</Btn>
                )}
                <Btn size="sm" variant={currentUser?.is_system_owner ? 'ghost' : 'primary'} onClick={requestSignOff} disabled={busy}>
                  Request Sign-off {currentUser?.is_system_owner ? 'from someone else' : ''}
                </Btn>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>Approval requested — waiting on a second person (not the requester) to approve.</p>
              <Btn size="sm" onClick={approveSignOff} disabled={busy}>Approve & Sign Off</Btn>
            </>
          )}
        </div>
      )}

      {take.status === 'signed_off' && (
        <p style={{ fontSize: 13, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Signed off {new Date(take.signed_off_at).toLocaleString()} — {variances.some(v => v.resolution_notes?.includes('stock_levels was NOT updated')) ? 'stock levels updated, except the counts in the red box above.' : 'stock levels updated.'}</p>
      )}

      {dutyFlags && dutyFlags.some(f => !['stock_not_applied', 'no_lpa_captured'].includes(f.type)) && (
        <div style={{ padding: 14, border: '1px solid #f59e0b', background: '#fffbeb', borderRadius: 'var(--radius)', marginTop: 12 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#92400e' }}>⚠ Duty status mismatch — needs a look</h4>
          <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 8px' }}>
            These items were counted somewhere that implies a different duty status than what's currently set on the item record.
            This can be genuine (some of the item is bonded, some already duty-paid) — nothing was changed automatically.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
            {dutyFlags.filter(f => !['stock_not_applied', 'no_lpa_captured'].includes(f.type)).map(f => (
              <li key={f.stock_item_id}>
                {f.item_name}: counted as <strong>{f.counted_duty_status.replace('_', ' ')}</strong>, item record says <strong>{f.item_record_is_bonded ? 'bonded' : 'duty paid'}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dutyFlags && dutyFlags.some(f => f.type === 'stock_not_applied') && (
        <div style={{ padding: 14, border: '1px solid #dc2626', background: '#fef2f2', borderRadius: 'var(--radius)', marginTop: 12 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991b1b' }}>⚠ Stock levels NOT updated — action needed</h4>
          <p style={{ fontSize: 12, color: '#991b1b', margin: '0 0 8px' }}>
            These counts had no bay/location picked, so they couldn't be safely applied to stock levels on sign-off.
            The quantities below are still missing from stock — use "Choose location &amp; put into stock" in the red box at the top of this stock take.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#991b1b' }}>
            {dutyFlags.filter(f => f.type === 'stock_not_applied').map(f => (
              <li key={f.stock_item_id}>
                {f.item_name}: <strong>{f.variance_quantity}</strong> still needs to be added
              </li>
            ))}
          </ul>
        </div>
      )}

      {dutyFlags && dutyFlags.some(f => f.type === 'no_lpa_captured') && (
        <div style={{ padding: 14, border: '1px solid #dc2626', background: '#fef2f2', borderRadius: 'var(--radius)', marginTop: 12 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991b1b' }}>⚠ Recorded with 0 litres — item has no ABV set</h4>
          <p style={{ fontSize: 12, color: '#991b1b', margin: '0 0 8px' }}>
            These gains/losses were correctly attributed to this W1 period, but the item has no ABV/bottle size set, so they contribute zero litres.
            Set the item's ABV on the Stock page, then ask for the movement's lpa to be backfilled and the period recalculated.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#991b1b' }}>
            {dutyFlags.filter(f => f.type === 'no_lpa_captured').map(f => (
              <li key={f.stock_item_id}>
                {f.item_name}: <strong>{f.variance_quantity}</strong> recorded with 0 litres
              </li>
            ))}
          </ul>
        </div>
      )}

      {uncountedRows.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{uncountedRows.length} previously-recorded item(s) in scope not yet counted.</p>
      )}

      {showAddCount && (
        <AddCountModal
          stockTakeId={id}
          locations={locations}
          onClose={() => { setShowAddCount(false); refetch(); }}
          onSaved={refetch}
        />
      )}

      {showScanPallet && (
        <ScanPalletModal
          stockTakeId={id}
          onClose={() => { setShowScanPallet(false); refetch(); }}
          onScanned={refetch}
        />
      )}

      {showCancel && (
        <Modal title="Cancel stock take" onClose={() => setShowCancel(false)} width={440}>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text)' }}>
            This abandons {take.stock_take_ref} — no counts recorded so far have touched stock levels, so there's nothing to reverse.
            It can't be undone once cancelled.
          </p>
          <Field label="Reason (optional)">
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              rows={3}
              placeholder="e.g. started against the wrong zone"
              style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical' }}
            />
          </Field>
          {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setShowCancel(false)}>Never mind</Btn>
            <Btn variant="danger" disabled={busy} onClick={cancelStockTake}>{busy ? 'Cancelling…' : 'Cancel stock take'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function StockTake() {
  const { data, refetch } = useApi('/stock-take');
  const takes = data?.stock_takes ?? data ?? [];
  const [showNew, setShowNew] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const { data: meData } = useApi('/settings/me');
  const currentUser = meData?.data ?? meData ?? null;

  if (activeId) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <StockTakeDetail id={activeId} onBack={() => { setActiveId(null); refetch(); }} currentUser={currentUser} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Stock Take</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{takes.length} stock take{takes.length === 1 ? '' : 's'}</p>
        </div>
        <Btn onClick={() => setShowNew(true)}>+ New Stock Take</Btn>
      </div>

      {takes.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          No stock takes yet. Start one to begin recording what's actually on the shelves.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '8px' }}>Ref</th>
              <th style={{ padding: '8px' }}>Type</th>
              <th style={{ padding: '8px' }}>Status</th>
              <th style={{ padding: '8px' }}>Started by</th>
              <th style={{ padding: '8px' }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {takes.map(t => (
              <tr key={t.id} onClick={() => setActiveId(t.id)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                <td style={{ padding: '8px', fontWeight: 600 }}>{t.stock_take_ref}</td>
                <td style={{ padding: '8px', textTransform: 'capitalize' }}>{t.take_type}</td>
                <td style={{ padding: '8px' }}><StatusBadge status={t.status} /></td>
                <td style={{ padding: '8px' }}>{t.initiated_by_name}</td>
                <td style={{ padding: '8px' }}>{new Date(t.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <NewStockTakeModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); refetch(); setActiveId(id); }} />
      )}
    </div>
  );
}
